import {
    ClaimLinkDialog,
    createClaimLinkDialogState,
    useCancelMemberInvitation,
    useFixMemberInvite,
    useOrgAuthStore,
    useOrgMembers,
    usePatchMember,
    useRegenerateMemberClaim,
    useResetMemberPassword
} from "@solomon-apps/shared";
import type {
    ClaimLinkDialogState,
    OrgMemberListItem,
    OrgMemberRole,
    OrgUserStatus
} from "@solomon-apps/shared";
import { StatusBadge } from "@solomon-apps/shared/src/components/StatusBadge";
import { Skeleton } from "@solomon-apps/shared/src/components/ui/skeleton";
import { useInvitationExpiry } from "@solomon-apps/shared/src/hooks/useInvitationExpiry";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import { Button } from "@solomon-labs/design/components/ui";
import { motion, useReducedMotion } from "motion/react";
import React, { useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { CLIENT_OAUTH_CONFIG } from "@/constants/oauthConfig";
import { resolveClientFormError } from "@/utils/clientFormErrors";

import {
    EditInvitationDialog,
    type InviteInput
} from "./Team/components/EditInvitationDialog";
import { InvitationLinkCard } from "./Team/components/InvitationLinkCard";
import { MemberAccountSection } from "./Team/components/MemberAccountSection";
import { ResetPasswordCard } from "./Team/components/ResetPasswordCard";
import { TeamMemberActionDialog } from "./Team/components/TeamMemberActionDialog";
import { TeamPageHeader } from "./Team/components/TeamPageHeader";
import {
    asTeamError,
    canEditMemberName,
    displayName,
    isLastActiveOwner,
    type MemberGuardContext,
    memberResetLockReason,
    memberStatusLabel,
    memberStatusTone,
    readNewInvitation,
    roleChangeLockReason,
    STATUS_TONE_TEXT,
    statusToggleLockReason,
    TEAM_PATH
} from "./Team/teamData";

type PendingAction =
    | { kind: "suspend" | "reactivate"; nextStatus: OrgUserStatus }
    | { kind: "resendInvitation" }
    | { kind: "cancelInvitation" }
    | { kind: "editInvitation" };

/**
 * Client Console Team member page (`/team/:memberUuid`), opened from a Team
 * table row. Laid out like Profile (`/settings`): the page heading (here with
 * a "Back to Team" link above the member's name), then two equal columns on
 * wide screens; the member's status sits beside their name. Left, the Account
 * section (Name, Email, Role as label-inside light-bar fields, with the member's access action under them);
 * right, for an Owner acting on an Active or Suspended member, the Reset
 * Password card, or, for an invitation Add Member (`/team/new`) just
 * created, its Invitation Link (handed over in router state). Below `lg` the
 * columns stack, Account first.
 *
 * The roster comes from the same `useOrgMembers` query as `/team`, so a
 * change here is current there. Only Owners manage the team; the org's last
 * active Owner cannot be demoted or suspended, and nobody suspends or resets
 * themselves (`teamData` guards, reasons on demand).
 */
export function TeamMemberPage(): React.ReactElement {
    const { memberUuid } = useParams<{ memberUuid: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const me = useOrgAuthStore((s) => s.me);
    const setMe = useOrgAuthStore((s) => s.setMe);
    const viewerIsOwner = me?.orgRole === "Owner";
    const reducedMotion = useReducedMotion() ?? false;

    const { members, isLoading, error, refetch } =
        useOrgMembers(CLIENT_OAUTH_CONFIG);
    const { patchMemberAsync, isPatching } =
        usePatchMember(CLIENT_OAUTH_CONFIG);
    const { regenerateClaimAsync, isRegenerating } =
        useRegenerateMemberClaim(CLIENT_OAUTH_CONFIG);
    const { resetPasswordAsync } = useResetMemberPassword(CLIENT_OAUTH_CONFIG);
    const { cancelInvitationAsync, isCancelling } =
        useCancelMemberInvitation(CLIENT_OAUTH_CONFIG);
    const { fixInviteAsync, isFixing } =
        useFixMemberInvite(CLIENT_OAUTH_CONFIG);

    const [pending, setPending] = useState<PendingAction | null>(null);
    const [mutationError, setMutationError] = useState<Error | null>(null);
    const [claimLink, setClaimLink] = useState<ClaimLinkDialogState | null>(
        null
    );
    // The claim link Add Member handed over. A resend or an edit voids it
    // (the server issues a fresh one), so those replace the history entry too.
    const newInvitation = readNewInvitation(location.state);
    const clearNewInvitation = (): void => {
        navigate(
            {
                pathname: location.pathname,
                search: location.search,
                hash: location.hash
            },
            { replace: true, state: null }
        );
    };

    // Every dialog here opens from a page button, never a Dialog trigger, so
    // Base UI has no trigger to hand focus back to. Remember the opener.
    const returnFocusRef = useRef<HTMLElement | null>(null);
    const rememberFocus = (): void => {
        const active = document.activeElement;
        returnFocusRef.current = active instanceof HTMLElement ? active : null;
    };
    const dialogFinalFocus = (): HTMLElement | boolean => {
        // A confirm that hands off to the claim-link dialog closes after that
        // dialog opened; leave focus inside the dialog that is now open.
        const host = document.activeElement?.closest("[role=dialog]");
        if (host?.hasAttribute("data-open")) return false;
        const opener = returnFocusRef.current;
        return opener?.isConnected ? opener : true;
    };

    const member = memberUuid
        ? members.find((m) => m.uuid === memberUuid)
        : undefined;
    // While a withdrawn invitation leaves the roster, the page is on its way
    // back to /team; hold the last member so it does not flash Not Found.
    const [lastMember, setLastMember] = useState<OrgMemberListItem>();
    if (member && member !== lastMember) setLastMember(member);
    const shown = member ?? (isCancelling ? lastMember : undefined);
    const { now } = useInvitationExpiry(
        shown?.status === "Invited" ? shown.inviteExpiresAt : null
    );

    const open = (action: PendingAction): void => {
        rememberFocus();
        setMutationError(null);
        setPending(action);
    };
    const closeDialog = (): void => setPending(null);

    const header = (
        title: React.ReactNode,
        status?: React.ReactNode
    ): React.ReactElement => <TeamPageHeader title={title} status={status} />;

    const pageMotion = {
        initial: reducedMotion ? false : { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        // Page enter: 400ms, named out-expo (rule: motion/named-easing),
        // the same as /team.
        transition: { duration: 0.4, ease: easings.outExpo }
    } as const;

    if (isLoading) {
        return (
            <div className="flex flex-col pt-3 md:pt-6 w-full min-h-full">
                {header(
                    <Skeleton
                        aria-hidden="true"
                        className="h-8 w-56 max-w-full rounded-none"
                    />
                )}
                <MemberPageSkeleton />
            </div>
        );
    }

    if (error) {
        return (
            <motion.div
                {...pageMotion}
                className="flex flex-col pt-3 md:pt-6 w-full min-h-full"
            >
                {header("Team Could Not Load")}
                <div className="px-4 md:px-6 py-6">
                    <Button
                        variant="raised"
                        interaction="lifted"
                        size="md"
                        onClick={() => void refetch()}
                        aria-label={`Retry: ${resolveClientFormError(
                            error,
                            "general",
                            "Refresh and try again."
                        )}`}
                    >
                        Retry
                    </Button>
                </div>
            </motion.div>
        );
    }

    if (!shown) {
        return (
            <motion.div
                {...pageMotion}
                className="flex flex-col pt-3 md:pt-6 w-full min-h-full"
            >
                {header("Member Not Found")}
            </motion.div>
        );
    }

    const isSelf = shown.uuid === me?.user.uuid;
    const guard: MemberGuardContext = {
        isSelf,
        viewerIsOwner,
        isLastOwner: isLastActiveOwner(shown, members)
    };
    const isMutating = isPatching || isCancelling || isFixing || isRegenerating;
    // An Owner resets for an Active or Suspended member; an invite has no
    // password yet (its link is the way in), and a Member manages nobody.
    const showResetCard = viewerIsOwner && shown.status !== "Invited";
    const invitationCard = shown.status === "Invited" ? newInvitation : null;

    const saveName = async (fullName: string): Promise<void> => {
        try {
            await patchMemberAsync({ uuid: shown.uuid, data: { fullName } });
        } catch (caught) {
            throw asTeamError(caught);
        }
        // Your own name also lives in the signed-in identity (the nav).
        if (isSelf && me) setMe({ ...me, user: { ...me.user, fullName } });
    };

    const saveRole = async (orgRole: OrgMemberRole): Promise<void> => {
        try {
            await patchMemberAsync({ uuid: shown.uuid, data: { orgRole } });
        } catch (caught) {
            throw asTeamError(caught);
        }
    };

    const confirmStatus = async (status: OrgUserStatus): Promise<void> => {
        if (status === "Invited") return;
        setMutationError(null);
        try {
            await patchMemberAsync({ uuid: shown.uuid, data: { status } });
            closeDialog();
        } catch (caught) {
            setMutationError(asTeamError(caught));
        }
    };

    const confirmResend = async (): Promise<void> => {
        setMutationError(null);
        try {
            const result = await regenerateClaimAsync(shown.uuid);
            closeDialog();
            clearNewInvitation();
            setClaimLink(createClaimLinkDialogState(result, "invitation"));
        } catch (caught) {
            setMutationError(asTeamError(caught));
        }
    };

    const confirmWithdraw = async (): Promise<void> => {
        setMutationError(null);
        try {
            await cancelInvitationAsync(shown.uuid);
            closeDialog();
            // The invitation is gone, and so is its page.
            navigate(TEAM_PATH, { replace: true });
        } catch (caught) {
            setMutationError(asTeamError(caught));
        }
    };

    const submitFixInvite = async (input: InviteInput): Promise<void> => {
        setMutationError(null);
        try {
            // `fix-invite` updates the fields AND re-issues the invitation
            // (a fresh link is generated server-side; the old one is voided).
            const result = await fixInviteAsync({
                uuid: shown.uuid,
                data: {
                    email: input.email,
                    fullName: input.fullName,
                    orgRole: input.orgRole
                }
            });
            closeDialog();
            clearNewInvitation();
            setClaimLink(createClaimLinkDialogState(result, "invitation"));
        } catch (caught) {
            setMutationError(asTeamError(caught));
        }
    };

    const statusAction =
        pending?.kind === "suspend" || pending?.kind === "reactivate"
            ? pending
            : null;

    return (
        <motion.div
            {...pageMotion}
            className="flex flex-col pt-3 md:pt-6 w-full min-h-full"
        >
            {header(
                displayName(shown),
                <StatusBadge
                    state={memberStatusTone(shown, now)}
                    label={memberStatusLabel(shown, now)}
                    labelClassName={
                        STATUS_TONE_TEXT[memberStatusTone(shown, now)]
                    }
                />
            )}

            {/* Profile's split: two equal columns on wide screens, the left
                closed by a full-height rule; stacked below lg. */}
            <div className="flex w-full flex-1 flex-col lg:flex-row">
                <div className="flex w-full flex-col py-3 md:py-6 lg:w-1/2 lg:border-r lg:border-line-base">
                    <MemberAccountSection
                        member={shown}
                        viewerIsOwner={viewerIsOwner}
                        canEditName={canEditMemberName(guard)}
                        roleLockReason={roleChangeLockReason(guard)}
                        statusLockReason={statusToggleLockReason(shown, guard)}
                        isMutating={isMutating}
                        onSaveName={saveName}
                        onSaveRole={saveRole}
                        onToggleStatus={() =>
                            open(
                                shown.status === "Suspended"
                                    ? {
                                          kind: "reactivate",
                                          nextStatus: "Active"
                                      }
                                    : {
                                          kind: "suspend",
                                          nextStatus: "Suspended"
                                      }
                            )
                        }
                        onEditInvite={() => open({ kind: "editInvitation" })}
                        onResendInvitation={() =>
                            open({ kind: "resendInvitation" })
                        }
                        onWithdrawInvitation={() =>
                            open({ kind: "cancelInvitation" })
                        }
                    />
                </div>
                {showResetCard && (
                    <div className="w-full px-4 py-3 md:px-6 md:py-6 lg:w-1/2">
                        <ResetPasswordCard
                            // A fresh card (and no stale link) per member.
                            key={shown.uuid}
                            lockReason={memberResetLockReason(shown, guard)}
                            onSend={() => resetPasswordAsync(shown.uuid)}
                            describeError={(caught) =>
                                asTeamError(caught).message
                            }
                        />
                    </div>
                )}
                {invitationCard && (
                    <div className="w-full px-4 py-3 md:px-6 md:py-6 lg:w-1/2">
                        <InvitationLinkCard invitation={invitationCard} />
                    </div>
                )}
            </div>

            <TeamMemberActionDialog
                open={statusAction !== null}
                onOpenChange={(next) => {
                    if (!next) closeDialog();
                }}
                kind={statusAction?.kind ?? "suspend"}
                member={shown}
                onConfirm={() =>
                    statusAction && void confirmStatus(statusAction.nextStatus)
                }
                isSubmitting={isPatching}
                error={mutationError}
                finalFocus={dialogFinalFocus}
            />

            <TeamMemberActionDialog
                open={pending?.kind === "resendInvitation"}
                onOpenChange={(next) => {
                    if (!next) closeDialog();
                }}
                kind="resendInvitation"
                member={shown}
                onConfirm={() => void confirmResend()}
                isSubmitting={isRegenerating}
                error={mutationError}
                finalFocus={dialogFinalFocus}
            />

            <TeamMemberActionDialog
                open={pending?.kind === "cancelInvitation"}
                onOpenChange={(next) => {
                    if (!next) closeDialog();
                }}
                kind="cancelInvitation"
                member={shown}
                onConfirm={() => void confirmWithdraw()}
                isSubmitting={isCancelling}
                error={mutationError}
                finalFocus={dialogFinalFocus}
            />

            <EditInvitationDialog
                open={pending?.kind === "editInvitation"}
                onOpenChange={(next) => {
                    if (!next) closeDialog();
                }}
                member={shown}
                onSubmit={(input) => void submitFixInvite(input)}
                isSubmitting={isFixing}
                error={mutationError}
                existingEmails={members.map((m) => m.email)}
                finalFocus={dialogFinalFocus}
            />

            <ClaimLinkDialog
                open={claimLink !== null}
                onOpenChange={(next) => {
                    if (next) return;
                    setClaimLink(null);
                    // The dialog unmounts with its url, before Base UI can
                    // return focus; hand it back to the opener here.
                    const opener = returnFocusRef.current;
                    if (opener?.isConnected) {
                        requestAnimationFrame(() => opener.focus());
                    }
                }}
                url={claimLink?.url ?? null}
                kind={claimLink?.kind}
                delivery={claimLink?.delivery ?? "url"}
            />
        </motion.div>
    );
}

/** First-load placeholder on the page's own two-column template. */
function MemberPageSkeleton(): React.ReactElement {
    return (
        <div
            aria-busy="true"
            aria-label="Loading member"
            className="flex w-full flex-1 flex-col lg:flex-row"
        >
            <div className="flex w-full flex-col gap-3 px-4 py-3 md:px-6 md:py-6 lg:w-1/2 lg:border-r lg:border-line-base">
                <Skeleton className="h-7 w-24 rounded-none" />
                {[0, 1, 2, 3].map((index) => (
                    <Skeleton
                        key={index}
                        className="h-10 w-full rounded-none"
                    />
                ))}
            </div>
            <div className="w-full px-4 py-3 md:px-6 md:py-6 lg:w-1/2" />
        </div>
    );
}
